import { FormikProvider, useField, useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Input, Label, Modal, Popover, QuillText, Select, Snippet } from '@treams/treams-ui';

function TalentReviewTemplateSelector() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [{ value }, , { setValue }] = useField('templateID');

	const query = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const options = _.sortBy(
		query.data?.talentReviewTemplates.edges.map(({ node }) => ({
			value: node.id,
			title: localize(node.title).text,
		})) ?? [],
		'label',
	);

	useEffect(() => {
		if (options.length === 1 && !value) {
			setValue(options[0].value);
		}
	}, [options.length]);

	if (!query.data) {
		return (
			<>
				<Label mb={2}>{t('talentReviews.create.templateLabel')}</Label>
				<Box width={200}>
					<Select.Skeleton />
				</Box>
			</>
		);
	}

	if (options.length === 1) {
		return null;
	}

	const description = query.data?.talentReviewTemplates.edges.find(({ node }) => node.id === value)
		?.node.description;

	return (
		<Box>
			<Label mb={2}>{t('talentReviews.create.templateLabel')}</Label>
			<Flex>
				<Select
					options={options}
					selectedValue={options.find((option) => option.value === value)?.value}
					onChange={(option) => setValue(option)}
					placeholder={t('talentReviews.create.templatePlaceholder')}
				/>
			</Flex>
			{description && (
				<Box mt={2}>
					<Snippet>
						<QuillText value={localize(description).text} />
					</Snippet>
				</Box>
			)}
		</Box>
	);
}

export default function CreateTalentReviewModal(props: {
	type: 'team' | 'department';
	groupID: string;
	returnUrl?: string;
}) {
	const { t } = useTranslation();

	const history = useHistory();

	const placeholder = t('talentReviews.create.namePlaceholder', { date: moment().format('L') });

	const [create] = graphql.useCreateTalentReviewMutation();

	const formik = useFormik({
		initialValues: {
			title: '',
			templateID: undefined as undefined | string,
		},
		onSubmit: async (values) => {
			const response = await create({
				variables: {
					input: {
						groupID: props.groupID,
						title: values.title || placeholder,
						templateID: values.templateID!,
					},
				},
			});

			history.push(`/talent-reviews/${response.data!.createTalentReview.talentReview.id}`);
		},
	});

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header
					title={t('talentReviews.create.title')}
					closeLink={props.returnUrl ?? `/${props.type}/${props.groupID}/talent-reviews`}
				/>
				<FormikProvider value={formik}>
					<Modal.Contents>
						<Box mb={4}>
							<Label mb={2}>{t('talentReviews.create.nameLabel')}</Label>
							<Input.Formik name="title" placeholder={placeholder} />
						</Box>

						<TalentReviewTemplateSelector />
					</Modal.Contents>

					<Modal.FormFooter
						cancelLink={props.returnUrl ?? `/${props.type}/${props.groupID}/talent-reviews`}
						onSave={() => formik.submitForm()}
						disabled={!formik.values.templateID}
					/>
				</FormikProvider>
			</Modal>
		</Popover>
	);
}
